import { useMutation, useQuery } from '@apollo/client';
import { NotificationsActive } from '@mui/icons-material';
import { Avatar, Checkbox, Grid, Icon, Paper } from '@mui/material';
import { ShibUser } from '@thro-types/users';

import { debounce } from 'lodash';
import { ReactElement, useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';
import { stringToColour } from 'utils';

import UPDATE_MESSAGING_SETTINGS_MUTATION from 'gql/mutations/updateMessagingSettingsForUser.gql';
import DEPARTMENTS_QUERY from 'gql/queries/getDepartments.gql';
import LOCKED_DEPARTMENTS_QUERY from 'gql/queries/getLockedDepartmentsForEntitlement.gql';
import MESSAGING_SETTINGS_QUERY from 'gql/queries/getMessagingSettingsForUser.gql';

import DetailPage from 'app/components/DetailPage';
import Subheading from 'app/components/DetailPage/components/Subheading';
import GQLGuard from 'app/components/GQLGuard';
import InfoBox from 'app/components/InfoBox';
import { actions as AlertActions } from 'app/containers/Dashboard/Alert/slice';
import { translations } from 'locales/i18n';
import { Department, LockedDepartments, UserSettings } from 'types/AppTypes';

interface Props {
  user: ShibUser;
}

export default function ProfilePage({ user }: Props): ReactElement {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const [departmentsInitialized, setDepartmentsInitialized] = useState(false);
  const [selectedDepartmentsNotifications, setSelectedDepartmentsNotifications] = useState<
    string[]
  >([]);
  const [selectedDepartmentsEmails, setSelectedDepartmentsEmails] = useState<string[]>([]);
  const [settingsId, setSettingsId] = useState<number>();
  const [emailsEnabled, setEmailsEnabled] = useState<boolean>();
  const departmentsQuery = useQuery<any, any>(DEPARTMENTS_QUERY, {
    variables: { notifications: true }
  });
  const messagingSettingsQuery = useQuery<any, any>(MESSAGING_SETTINGS_QUERY, {
    variables: { userId: user.uid }
  });
  const lockedDepartmentsQuery = useQuery<any, any>(LOCKED_DEPARTMENTS_QUERY, {
    variables: { entitlement: user.role }
  });
  const [updateMessagingSettings] = useMutation(UPDATE_MESSAGING_SETTINGS_MUTATION);
  const [avatarBackground, color] = stringToColour(user.email);

  const handleClick = (type: 'notifications' | 'emails', id: string) => {
    let saveValue;
    if (type === 'notifications') {
      if (selectedDepartmentsNotifications.includes(id)) {
        saveValue = selectedDepartmentsNotifications.filter(d => d !== id);
        setSelectedDepartmentsNotifications(saveValue);
      } else {
        saveValue = [...selectedDepartmentsNotifications, id];
        setSelectedDepartmentsNotifications(saveValue);
      }
      handleSave({
        notificationCategories: saveValue.map(v => parseInt(v, 10))
      });
    }

    if (type === 'emails') {
      if (selectedDepartmentsEmails.includes(id)) {
        saveValue = selectedDepartmentsEmails.filter(d => d !== id);
        setSelectedDepartmentsEmails(saveValue);
      } else {
        saveValue = [...selectedDepartmentsEmails, id];
        setSelectedDepartmentsEmails(saveValue);
      }
      handleSave({
        emailCategories: saveValue.map(v => parseInt(v, 10))
      });
    }
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const handleSave = useCallback(
    debounce(async saveObject => {
      updateMessagingSettings({
        variables: {
          id: settingsId?.toString(),
          settings: {
            ...saveObject
          }
        }
      }).catch(e => {
        dispatch(AlertActions.error(`Error saving messaging settings: ${JSON.stringify(e)}`));
      });
    }, 200),
    [settingsId]
  );

  const SideInfo = () => (
    <Grid container className="profile__side-info">
      <Grid container justifyContent="center">
        <Avatar
          component={Paper}
          className="profile__side-info__avatar"
          style={{
            backgroundColor: avatarBackground,
            color
          }}
        >
          {user.firstName.charAt(0).toUpperCase()}
          {user.lastName.charAt(0).toUpperCase()}
        </Avatar>
      </Grid>
      <Grid container justifyContent="center" className="profile__side-info__name">
        {user.firstName} {user.lastName}
      </Grid>
      <Grid container justifyContent="center" className="profile__side-info__role">
        {user.role}
      </Grid>
      <Grid container>
        <Grid container className="profile__side-info__contact">
          <Grid item xs={12} md={4} className="profile__side-info__contact__label">
            {t(translations.common.email_short())}:
          </Grid>
          <Grid item xs={12} md={8} className="profile__side-info__contact__content">
            {user.email}
          </Grid>
        </Grid>
        <Grid container>
          <Grid item xs={12} md={4} className="profile__side-info__contact__label">
            {t(translations.common.semester())}:
          </Grid>
          <Grid item xs={12} md={8} className="profile__side-info__contact__content">
            {user.semester || '---'}
          </Grid>
        </Grid>
        <Grid container>
          <Grid item xs={12} md={4} className="profile__side-info__contact__label">
            {t(translations.common.course())}:
          </Grid>
          <Grid item xs={12} md={8} className="profile__side-info__contact__content">
            {(user.communities && user.communities[0]) || '---'}
          </Grid>
        </Grid>
      </Grid>
    </Grid>
  );

  return (
    <DetailPage className="profile" sideInfo={<SideInfo />}>
      <GQLGuard query={[departmentsQuery, lockedDepartmentsQuery, messagingSettingsQuery]}>
        {([
          { departments },
          { lockedDepartmentsForEntitlement },
          { getMessagingSettingsForUser: settings }
        ]: [
          { departments: Department[] },
          { lockedDepartmentsForEntitlement: LockedDepartments },
          {
            getMessagingSettingsForUser: UserSettings;
          }
        ]) => {
          if (!departmentsInitialized) {
            setDepartmentsInitialized(true);
            setSelectedDepartmentsNotifications(settings.notificationCategories.map(c => c.id));
            setSelectedDepartmentsEmails(settings.emailCategories.map(c => c.id));
          }
          if (!settingsId) {
            setSettingsId(settings.id);
            setEmailsEnabled(settings.active);
          }
          return (
            <>
              <Grid container className="profile__info"></Grid>
              <Subheading content={t(translations.pages.profile.notificationsSubheader())} />
              <Grid container className="profile__info" paddingBottom={2}>
                <InfoBox
                  type="warning"
                  title={t(translations.pages.profile.autosaveInfo.title())}
                  collapsed
                  expandable
                >
                  {t(translations.pages.profile.autosaveInfo.content())}
                </InfoBox>
              </Grid>
              <Grid container className="profile__infopadding profile__info">
                <InfoBox
                  type="warning"
                  title={t(translations.pages.profile.summaryInfoBlockedDepartments.title())}
                >
                  {t(translations.pages.profile.summaryInfoBlockedDepartments.content())}
                </InfoBox>
              </Grid>
              <Grid container className="profile__categories profile__infopadding">
                <Grid container>
                  <Grid item xs={3} />
                  <Grid item xs={2} className="profile__categories__row__checkbox">
                    <Grid container className="shibUserDetail__categories__heading">
                      <Grid item className="shibUserDetail__categories__heading__icon">
                        <NotificationsActive fontSize="medium" />
                      </Grid>
                      <Grid item className="shibUserDetail__categories__heading__title">
                        {t(translations.pages.profile.notificationSettingsHeaders.bell())}
                      </Grid>
                    </Grid>
                  </Grid>
                  <Grid item xs={2} className="profile__categories__row__checkbox">
                    <Grid container className="shibUserDetail__categories__heading">
                      <Grid item className="shibUserDetail__categories__heading__icon2">
                        <Icon className="fas fa-envelope" style={{ justifyContent: 'flex-end' }} />
                      </Grid>
                      <Grid item className="shibUserDetail__categories__heading__title">
                        {t(translations.pages.profile.notificationSettingsHeaders.email())}
                      </Grid>
                    </Grid>
                  </Grid>
                </Grid>
                {departments.map(d => {
                  const isLocked =
                    lockedDepartmentsForEntitlement.departments.findIndex(ld => ld.id === d.id) !==
                    -1;
                  return (
                    <Grid container className="profile__categories__row" key={d.id}>
                      <Grid item xs={3} className="profile__categories__row__label">
                        {d.title}
                      </Grid>
                      <Grid item xs={2} className="profile__categories__row__checkbox">
                        <Checkbox
                          color="primary"
                          disabled={isLocked}
                          checked={isLocked || selectedDepartmentsNotifications.includes(d.id)}
                          onClick={() => handleClick('notifications', d.id)}
                        />
                      </Grid>
                      <Grid item xs={2} className="profile__categories__row__checkbox">
                        <Checkbox
                          color="primary"
                          disabled={isLocked}
                          checked={isLocked || selectedDepartmentsEmails.includes(d.id)}
                          onClick={() => handleClick('emails', d.id)}
                        />
                      </Grid>
                    </Grid>
                  );
                })}
              </Grid>
            </>
          );
        }}
      </GQLGuard>
    </DetailPage>
  );
}
